<script>
import Container from "~/components/layout/Container.vue";
import Section from "~/components/layout/Section.vue";
import Spinner from "~/components/misc/Spinner.vue";
import { useWholesaleDiscount } from "~/composables/useWholesaleDiscount";
import { useCartCalculator } from "~/composables/useCartCalculator";
import NonEuShippingWarning from "~/components/misc/NonEuShippingWarning.vue";
import { useDetectedCountry } from "~/composables/useDetectedCountry";
import { getPostalShippingCost } from "~/utils/postalShipping";

export default {
	components: { Spinner, Container, Section, NonEuShippingWarning },
	setup() {
		return useDetectedCountry()
	},
	data() {
		return {
			config: useRuntimeConfig(),
			cartData: [],
			location: "",
			cartLoadError: "",
			processing: false,
			checkoutError: "",
			loading: true,
			error: false,
			offers: [],
			postalShippings: [],
			newsletter: false,
			wholesaleDiscount: useWholesaleDiscount(),
			cartCalculator: useCartCalculator(),
			couponCode: "",
			couponDiscount: 0,
			couponApplied: false,
			couponError: "",
			couponLoading: false,
			address: {
				firstName: "",
				lastName: "",
				email: "",
				phone: "",
				region: "",
				city: "",
				zip: "",
				address1: ""
			}
		}
	},
	async mounted() {
		void this.detectCountry().then(country => {
			if (!this.location) this.location = country
		})
		try {
			if (process.client) {
				const cartStorage = localStorage.getItem("neuronoirCart")
				if (!cartStorage) {
					return
				}

				const cartData = JSON.parse(cartStorage)

				this.cartData = await Promise.all(cartData.map(async item => {
					if (item.product.type !== 'T-Shirt') {
						return item
					}

					let response = await fetch(this.config.public.baseURL + "/api/products/" + item.product.productId)
					let backendProduct = await response.json()

					let variant = backendProduct.variants.filter(variant => {
						if (!variant.is_enabled) {
							return false
						}

						const hasVariant = variant.options.filter(option => {
							return option == item.selectedSize.id
						})

						return hasVariant.length > 0
					})

					item.variant = variant[0]
					return item
				}))

				await this.getOffers();

				const response = await GqlGetShippings();
				this.postalShippings = response.shippings;
			}

		} catch {
			this.cartLoadError = "We couldn't load your cart details. Please refresh the page to try again."
		} finally {
			this.loading = false
		}
	},
	computed: {
		hasPreOrders() {
			let preOrderFound = false;
			this.cartData.forEach(product => {
				product.product.properties.forEach(property => {
					if (property.key.includes("Pre-Order")) {
						preOrderFound = true;
						return;
					}
				})
			})
			return preOrderFound;
		},
		orderPayload() {
			const address = this.address
			address.country = this.location

			return {
				address: this.address,
				lineItems: this.lineItems,
				newsletter: this.newsletter,
        coupon: this.couponCode.trim()
			}
		},
		lineItems() {
			if (!this.cartData) {
				return []
			}

			if (!this.cartData.length) {
				return []
			}

			return this.cartData.map(item => {
				if (item.product.type === 'T-Shirt') {
					return {
						productId: item.product.productId,
						variantId: item.variant.id,
						quantity: item.quantity,
						type: item.product.type
					}
				}

				const postalShippingCost = getPostalShippingCost(this.postalShippings, this.location)
				if (postalShippingCost === null) {
					throw new Error("A shipping rate is required for the selected destination.")
				}
				let selectedSize = "N/A"

				if (item.selectedSize) {
					selectedSize = item.selectedSize.title
				}

				return {
					productId: item.product.productId,
					variantId: null,
					quantity: item.quantity,
					type: item.product.type,
					price: this.getOfferPrice(item.product.productId),
					shipping: postalShippingCost,
					selectedSize: selectedSize
				}
			})
		},
		subTotal() {
			if (!this.cartData.length) return 0;
			
			// Convert to euros (cents)
			const totalInEuroCents = this.cartData.reduce((sum, item) => {
				if (item.quantity >= 10) {
					// Apply wholesale discount
					if (item.product.type === 'T-Shirt') {
						const originalPrice = Number(item.variant.price);
						const discountPercentage = this.wholesaleDiscount.getDiscount(item.quantity);
						const discountedPrice = originalPrice * (1 - discountPercentage);
						return sum + (discountedPrice * item.quantity);
					} else {
						const originalPrice = this.getOfferPrice(item.product.productId);
						const discountPercentage = this.wholesaleDiscount.getDiscount(item.quantity);
						const discountedPrice = originalPrice * (1 - discountPercentage);
						return sum + (discountedPrice * item.quantity);
					}
				} else {
					// Regular pricing
					if (item.product.type === 'T-Shirt') {
						return sum + (item.quantity * Number(item.variant.price));
					} else {
						return sum + (item.quantity * this.getOfferPrice(item.product.productId));
					}
				}
			}, 0);
			
			return totalInEuroCents;
		},
		totalSavings() {
			if (!this.cartData.length) return 0;
			
			return this.cartData.reduce((savings, item) => {
				if (item.quantity >= 10) {
					let originalTotal = 0;
					if (item.product.type === 'T-Shirt') {
						originalTotal = item.quantity * Number(item.variant.price);
					} else {
						originalTotal = item.quantity * this.getOfferPrice(item.product.productId);
					}
					
					// Calculate discounted total
					let discountedTotal = 0;
					if (item.product.type === 'T-Shirt') {
						const originalPrice = Number(item.variant.price);
						const discountPercentage = this.wholesaleDiscount.getDiscount(item.quantity);
						const discountedPrice = originalPrice * (1 - discountPercentage);
						discountedTotal = discountedPrice * item.quantity;
					} else {
						const originalPrice = this.getOfferPrice(item.product.productId);
						const discountPercentage = this.wholesaleDiscount.getDiscount(item.quantity);
						const discountedPrice = originalPrice * (1 - discountPercentage);
						discountedTotal = discountedPrice * item.quantity;
					}
					
					return savings + (originalTotal - discountedTotal);
				}
				return savings;
			}, 0);
		},
		shippingCost() {
			if (!this.location) {
				return 0
			}

			let totalQuantity = 0
			let postalShippingItemsCount = 0
			this.cartData.forEach(item => {
				if (item.product.type === 'T-Shirt') {
					totalQuantity += item.quantity
				} else {
					postalShippingItemsCount++
				}
			})

			let firstItem = 0
			let additionalItems = 0
			if (totalQuantity >= 1) {
				firstItem = 1
			}
			additionalItems = totalQuantity - firstItem

			let manualFulfilmentShippingCost = 0 //TODO:

			if (postalShippingItemsCount >= 1) {
				manualFulfilmentShippingCost = getPostalShippingCost(this.postalShippings, this.location)
			}

			return manualFulfilmentShippingCost
		}
	},
	methods: {
        hasDiscount(productId) {
            const offers = this.offers.filter(offer => {
                return offer.productId === productId
            })

            if (!offers.length) {
                return false
            }

            return !!offers[0].discountPrice
        },
        getSubtotalPrice() {
            if (this.location == 'RO') {
                return `${(this.subTotal * 5.2 / 100).toFixed(0) } RON`
            }
            return `€${(this.subTotal / 100).toFixed(2) }`
        },
        getSavingsPrice() {
            if (this.location == 'RO') {
                return `${(this.totalSavings * 5.2 / 100).toFixed(0) } RON`
            }
            return `€${(this.totalSavings / 100).toFixed(2) }`
        },
        getShippingCostPrice() {
            if (this.shippingCost === null) return "Unavailable for this country"
            if (this.location == 'RO') {
                return `${(this.shippingCost * 5.2 / 100).toFixed(0) } RON`
            }
            return `€${(this.shippingCost / 100).toFixed(2) }`
        },
        getTotalPrice() {
            if (this.shippingCost === null) return "Awaiting shipping rate"
            const total = this.subTotal + this.shippingCost;
            const discountAmount = total * (this.couponDiscount / 100);
            const finalTotal = total - discountAmount;
            
            if (this.location == 'RO') {
                return `${(finalTotal * 5.2 / 100).toFixed(0) } RON`
            }
            return `€${(finalTotal / 100).toFixed(2) }`
        },
        getCouponDiscountAmount() {
            if (this.shippingCost === null) return "—"
            const total = this.subTotal + this.shippingCost;
            const discountAmount = total * (this.couponDiscount / 100);
            
            if (this.location == 'RO') {
                return `${(discountAmount * 5.2 / 100).toFixed(0) } RON`
            }
            return `€${(discountAmount / 100).toFixed(2) }`
        },
        getPrintifyPrice(item) {
            const originalPrice = Number(item.variant.price);
            let finalPrice = originalPrice;
            
            // Apply wholesale discount if quantity >= 10
            if (item.quantity >= 10) {
                const discountPercentage = this.wholesaleDiscount.getDiscount(item.quantity);
                finalPrice = originalPrice * (1 - discountPercentage);
            }
            
            const totalPrice = finalPrice * item.quantity;
            
            if (this.location == 'RO') {
                return `${((totalPrice * 5.2) / 100).toFixed(0)} RON`
            }
            return `€${(totalPrice / 100).toFixed(2)}`
        },
        getOfferPriceString(item) {
            const originalPrice = this.getOfferPrice(item.product.productId);
            let finalPrice = originalPrice;
            
            // Apply wholesale discount if quantity >= 10
            if (item.quantity >= 10) {
                const discountPercentage = this.wholesaleDiscount.getDiscount(item.quantity);
                finalPrice = originalPrice * (1 - discountPercentage);
            }
            
            const totalPrice = finalPrice * item.quantity;
            
            if (this.location == 'RO') {
                return `${((totalPrice * 5.2) / 100).toFixed(0)} RON`
            }
            return `€${(totalPrice / 100).toFixed(2)}`
        },
        getItemDiscountPercentage(item) {
            if (item.quantity >= 10) {
                return this.wholesaleDiscount.getDiscountPercentage(item.quantity);
            }
            return 0;
        },
		getOfferPrice(productId) {
			if (this.offers.length === 0) {
				return 0
			}

			console.log(productId)

			const offers = this.offers.filter(offer => {
				return offer.productId === productId
			})

			if (offers.length === 0) {
				return 0
			}

            if (!!offers[0].discountPrice) {
                return offers[0].discountPrice
            }

			return offers[0].price
		},
		async getOffers() {
			const { offers } = await GqlGetOffers();
			this.offers = offers
		},
		async applyCoupon() {
			if (!this.couponCode.trim()) {
				this.couponError = "Please enter a coupon code";
				return;
			}
			
			this.couponLoading = true;
			this.couponError = "";
			this.couponApplied = false;
			
			try {
				const response = await fetch(`${this.config.public.baseURL}/api/coupons/${this.couponCode.trim()}`);
				
				if (!response.ok) {
					throw new Error("Invalid coupon code");
				}
				
				const couponData = await response.json();
				this.couponDiscount = couponData.discount_percentage || 0;
				this.couponApplied = true;
				this.couponError = "";
			} catch (error) {
				this.couponError = "Invalid coupon code";
				this.couponDiscount = 0;
				this.couponApplied = false;
			} finally {
				this.couponLoading = false;
			}
		},
		async checkout() {
			this.checkoutError = ""
			if (this.loading || this.cartLoadError) {
				this.checkoutError = "Please wait for your cart details to load, or refresh the page to try again."
				return
			}
			if (this.cartData.some(item => item.product.type !== "T-Shirt")
				&& getPostalShippingCost(this.postalShippings, this.location) === null) {
				this.checkoutError = "We couldn't find a shipping rate for your selected destination. Please choose another country or contact us for help."
				return
			}
			this.processing = true
			let payload = null

			try {
				const response = await fetch(this.config.public.baseURL + "/api/orders", {
					method: 'POST',
					body: JSON.stringify(this.orderPayload),
					headers: {
						'Content-Type': 'application/json'
					}
				})
				payload = await response.json()
			} catch (e) {
				this.checkoutError = "We couldn't start payment. Please try again."
				return
			} finally {
				this.processing = false
			}

			if (!payload || !payload.url) {
				this.checkoutError = "We couldn't start payment. Please try again."
				return;
			}

			localStorage.setItem('neuronoirCart', '')
			window.location = payload.url
		}
	}
}
</script>

<template>
	<Container class="bg-neurodarkgreyvar">
		<div class="py-10">
			<Section title="Checkout"
				description="Please add your shipping details so that we are able to process your order." />
			<NonEuShippingWarning :country="detectedCountry" />
			<p v-if="cartLoadError" role="alert" class="text-red-400 mb-5">{{ cartLoadError }}</p>
			<div v-if="hasPreOrders" class="text-white py-3 mb-5 border-neurolink border rounded px-3">
				You are about to purchase one or multiple items that are currently in Pre-Order. Those will be shipped
				as soon as they are released.
			</div>
			<div class="grid grid-cols-2 gap-4">
				<div class="sm:col-span-1 col-span-2 order-last sm:order-first">
					<div class="font-semibold text-zinc-100 mb-5 border-b border-zinc-800 pb-3">Shipping Details</div>

					<form @submit.prevent="checkout" id="createOrder"
						class="max-w-screen-md grid sm:grid-cols-2 gap-4 mx-auto">
						<p v-if="checkoutError" role="alert" class="sm:col-span-2 text-red-400">{{ checkoutError }}</p>
						<div>
							<label for="first-name" class="inline-block text-neurolightgrey text-sm sm:text-base mb-2">
								First name*
							</label>
							<input name="first-name" v-model="address.firstName" required
								class="w-full bg-gray-50 text-neurolightgrey border focus:ring ring-neuroprimary rounded outline-none transition duration-100 px-3 py-2">
						</div>

						<div>
							<label for="last-name" class="inline-block text-neurolightgrey text-sm sm:text-base mb-2">
								Last name*
							</label>
							<input name="last-name" v-model="address.lastName" required
								class="w-full bg-gray-50 text-neurolightgrey border focus:ring ring-neuroprimary rounded outline-none transition duration-100 px-3 py-2">
						</div>

						<div class="sm:col-span-2">
							<label for="email" class="inline-block text-neurolightgrey text-sm sm:text-base mb-2">
								Email*
							</label>
							<input name="email" v-model="address.email" required type="email"
								class="w-full bg-gray-50 text-neurolightgrey border focus:ring ring-neuroprimary rounded outline-none transition duration-100 px-3 py-2">
              <nuxt-link to="/privacy" class="text-neurolink">Privacy Policy</nuxt-link>
						</div>

						<div class="sm:col-span-2">
							<label for="phone" class="inline-block text-neurolightgrey text-sm sm:text-base mb-2">
								Phone number*
							</label>
							<input name="phone" v-model="address.phone" required
								class="w-full bg-gray-50 text-neurolightgrey border focus:ring ring-neuroprimary rounded outline-none transition duration-100 px-3 py-2">
						</div>

						<div>
							<label for="country" class="inline-block text-neurolightgrey text-sm sm:text-base mb-2">
								Country*
							</label>
							<select v-model="location" id="shipping-country" name="country" required
								class="w-full bg-gray-50 text-neurodarkgrey border focus:ring ring-neuroprimary rounded outline-none transition duration-100 px-3 py-2">
								<option value="RO">Romania</option>
								<option value="AL">Albania</option>
								<option value="AT">Austria</option>
								<option value="BE">Belgium</option>
								<option value="BA">Bosnia and Herzegovina</option>
								<option value="BG">Bulgaria</option>
								<option value="HR">Croatia (Hrvatska)</option>
								<option value="CY">Cyprus</option>
								<option value="CZ">Czech Republic</option>
								<option value="FR">France</option>
								<option value="DE">Germany</option>
								<option value="GR">Greece</option>
								<option value="HU">Hungary</option>
								<option value="IT">Italy</option>
								<option value="LU">Luxembourg</option>
								<option value="MK">Macedonia</option>
								<option value="MT">Malta</option>
								<option value="MD">Moldova</option>
								<option value="NL">Netherlands</option>
								<option value="PL">Poland</option>
								<option value="PT">Portugal</option>
								<option value="SK">Slovakia</option>
								<option value="SI">Slovenia</option>
								<option value="ES">Spain</option>
								<option value="DK">Denmark</option>
								<option value="EE">Estonia</option>
								<option value="FI">Finland</option>
								<option value="IE">Ireland</option>
								<option value="LV">Latvia</option>
								<option value="LT">Lithuania</option>
								<option value="SE">Sweden</option>
							</select>
						</div>

						<div>
							<label for="region" class="inline-block text-neurolightgrey text-sm sm:text-base mb-2">
								Region/State*
							</label>
							<input name="region" v-model="address.region" required
								class="w-full bg-gray-50 text-neurolightgrey border focus:ring ring-neuroprimary rounded outline-none transition duration-100 px-3 py-2">
						</div>

						<div>
							<label for="city" class="inline-block text-neurolightgrey text-sm sm:text-base mb-2">
								City*
							</label>
							<input name="city" v-model="address.city" required
								class="w-full bg-gray-50 text-neurolightgrey border focus:ring ring-neuroprimary rounded outline-none transition duration-100 px-3 py-2">
						</div>

						<div>
							<label for="postal-code" class="inline-block text-neurolightgrey text-sm sm:text-base mb-2">
								Postal Code*
							</label>
							<input name="postal-code" v-model="address.zip" required
								class="w-full bg-gray-50 text-neurolightgrey border focus:ring ring-neuroprimary rounded outline-none transition duration-100 px-3 py-2">
						</div>

						<div class="sm:col-span-2">
							<label for="address" class="inline-block text-neurolightgrey text-sm sm:text-base mb-2">
								Address*
							</label>
							<input name="address" v-model="address.address1" required
								class="w-full bg-gray-50 text-neurolightgrey border focus:ring ring-neuroprimary rounded outline-none transition duration-100 px-3 py-2">
						</div>

						<div class="sm:col-span-2">
							<input type="checkbox" name="newsletter" v-model="newsletter"
								class="px-3 py-2">
							<label for="newsletter" class="pl-2 inline-block text-neurolightgrey text-sm sm:text-base mb-2">
								Yes! Keep me updated with special offers and news.
							</label>
						</div>

						<div class="sm:col-span-2 flex justify-between items-center">
							<button v-if="!processing" type="submit"
								class="bg-neuroprimary font-light px-6 py-2 text-white rounded w-full block text-center mt-5">
								Continue to payment
							</button>
							<span v-if="processing" type="submit"
								class="bg-neurodarkgreyvar font-light px-6 py-2 text-white rounded w-full block text-center mt-5">
								Processing ...
							</span>
						</div>
					</form>
				</div>
				<div class="sm:col-span-1 col-span-2">
					<div class="font-semibold text-zinc-100 mb-5 border-b border-zinc-800 pb-3">Order Summary</div>
					<Spinner v-if="loading"></Spinner>
					<div class="flex flex-col gap-4 md:gap-6 mb-6 sm:mb-8" v-for="item in cartData">
						<!-- product - start -->
						<div
							class="flex flex-wrap border border-zinc-700 rounded-lg overflow-hidden gap-x-4 sm:gap-y-4 lg:gap-6">
							<a href="#"
								class="group w-32 sm:w-40 h-32 sm:h-40 block bg-zinc-100 overflow-hidden relative">
								<nuxt-img :src="item.product.image.url" loading="lazy"
									class="w-full h-full object-cover object-center group-hover:scale-110 transition duration-200" />
							</a>

							<div class="flex flex-col justify-between flex-1 py-4">
								<div>
									<a href="#"
										class="inline-block text-white hover:opacity-80 text-lg lg:text-xl font-bold transition duration-100 mb-1">
										{{ item.product.title }}
									</a>

									<h5 v-if="item.product.type === 'T-Shirt'" class="block text-neurolightgrey">Size:
										{{ item.selectedSize.title }}</h5>
									<h6 v-if="item.product.type === 'T Shirt'" class="block text-neurolightgrey">Size:
										{{ item.selectedSize.title }}</h6>
									<span class="block text-neurolightgrey">Qty: {{ item.quantity }}</span>
								</div>
							</div>

							<div
								class="w-full sm:w-auto flex justify-between border-t sm:border-none p-4 sm:pl-0 lg:p-6 lg:pl-0">
								<div class="pt-3 md:pt-2 sm:ml-4 ml-0">
									<span class="block text-zinc-100 md:text-lg font-bold"
										v-if="item.product.type === 'T-Shirt'">
                                        {{getPrintifyPrice(item)}}
									</span>
									<span class="block text-zinc-100 md:text-lg font-bold"
										v-if="item.product.type !== 'T-Shirt'">
                                        {{getOfferPriceString(item)}}
									</span>
									<div v-if="getItemDiscountPercentage(item) > 0" class="text-green-400 text-sm">
										Wholesale discount: {{ getItemDiscountPercentage(item) }}% OFF
									</div>
								</div>
							</div>
						</div>
						<!-- product - end -->
					</div>
					<div class="flex flex-col items-end gap-4">
						<div class="float-right pb-10">
							<div class="bg-neurodarkgrey rounded px-3 py-3">
								<div class="flex justify-between">
									<span class="text-white opacity-50 pr-10">Subtotal</span>
									<span class="text-white opacity-50">{{getSubtotalPrice()}}</span>
								</div>
								<div v-if="totalSavings > 0" class="flex justify-between">
									<span class="text-green-400 pr-10">Wholesale Savings</span>
									<span class="text-green-400">-{{getSavingsPrice()}}</span>
								</div>
								<div class="flex justify-between">
									<span class="text-white opacity-50 pr-10">Shipping in {{ location }}</span>
									<span class="text-white opacity-50">{{getShippingCostPrice()}}</span>
								</div>
								<div v-if="couponApplied" class="flex justify-between">
									<span class="text-green-400 pr-10">Coupon Discount ({{couponDiscount}}%)</span>
									<span class="text-green-400">-{{getCouponDiscountAmount()}}</span>
								</div>
								<hr class="block my-5 opacity-10">
								<div class="flex justify-between">
									<span class="text-white font-bold pr-10">Total</span>
									<span class="text-white font-bold">{{getTotalPrice()}}</span>
								</div>
								<div class="mt-4">
									<div class="flex items-center">
										<input 
											type="text" 
											v-model="couponCode" 
											placeholder="Coupon code" 
											class="w-full bg-gray-50 text-neurolightgrey border focus:ring ring-neuroprimary rounded-l outline-none transition duration-100 px-3 py-2"
											:disabled="couponLoading"
										>
										<button 
											@click="applyCoupon" 
											class="bg-neuroprimary font-light px-4 py-2 text-white rounded-r"
											:disabled="couponLoading"
										>
											<span v-if="!couponLoading">Apply</span>
											<span v-else>...</span>
										</button>
									</div>
									<div v-if="couponError" class="text-red-500 text-sm mt-1">
										{{ couponError }}
									</div>
									<div v-if="couponApplied" class="text-green-400 text-sm mt-1">
										Coupon applied successfully!
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</Container>
</template>

<style scoped></style>
